// ─────────────────────────────────────────────────────────────────────────────
//  src/data/content.js
//  Single source of truth — EN & AR  |  Synced with test.sityx.com content
// ─────────────────────────────────────────────────────────────────────────────

export const translations = {
  // ══════════════════════════════════════════════════════════
  //  ENGLISH
  // ══════════════════════════════════════════════════════════
  en: {
    // ── NAV ──────────────────────────────────────────────────
    nav: {
      about:       'About',
      services:    'Services',
      hosting:     'Hosting Plans',
      development: 'Infrastructure',
      domain:      'Domain',
      faq:         'FAQ',
      contact:     'Support',
    },

    // ── HERO ─────────────────────────────────────────────────
    hero: {
      badge:    'Cloud Hosting · Web Development · Software',
      title1:   'Ultra-Fast Cloud Hosting',
      title2:   '& Software Development',
      subtitle:
        'State-of-the-art NVMe infrastructure with predictive AI security, plus full-service web and software development — built for businesses that cannot afford downtime.',
      cta1: 'View Hosting Plans',
      cta2: 'Talk to Support',
    },

    // ── STATS ────────────────────────────────────────────────
    stats: [
      { value: '99.9%', label: 'Uptime SLA' },
      { value: 'NVMe',  label: 'Gen4 Storage' },
      { value: 'AI',    label: 'Threat Shield' },
      { value: '24/7',  label: 'Human Support' },
    ],

    // ── SERVICES ─────────────────────────────────────────────
    services: {
      label:    'What We Offer',
      title:    'A Complete Digital Ecosystem',
      subtitle: 'More than server space — an intelligent platform that actively protects, accelerates, and builds your online business.',
      items: [
        {
          icon:   '☁️',
          title:  'AI Cloud Hosting',
          desc:   'Our ML algorithms continuously monitor hardware health, auto-allocate RAM & CPU during traffic spikes, and block threats before they reach your data.',
          link:   'View plans',
          href:   '#hosting',
          accent: '#00e5ff',
        },
        {
          icon:   '🛡️',
          title:  'Predictive AI Security',
          desc:   'Security protocols that evolve daily. Our AI learns from global threat networks to anticipate DDoS attacks, malware injections, and brute-force attempts.',
          link:   'Learn more',
          href:   '#security',
          accent: '#7c4dff',
        },
        {
          icon:   '🌐',
          title:  'Domains & SSL',
          desc:   'Register your domain from hundreds of TLDs and secure every connection with free SSL certificates — managed from one unified dashboard.',
          link:   'Search domain',
          href:   '#domain',
          accent: '#00ff9d',
        },
        {
          icon:   '💻',
          title:  'Web Development',
          desc:   'Custom websites, landing pages, and full-stack web applications built with modern frameworks — React, Laravel, WordPress, and more.',
          link:   'Get a quote',
          href:   '#development',
          accent: '#00e5ff',
        },
        {
          icon:   '📱',
          title:  'Mobile App Development',
          desc:   'Cross-platform and native mobile applications for iOS and Android, designed for performance, usability, and scalability.',
          link:   'Get a quote',
          href:   '#development',
          accent: '#7c4dff',
        },
        {
          icon:   '🤖',
          title:  'AI Systems Development',
          desc:   'Custom artificial intelligence integrations, automation pipelines, and ML-powered tools tailored to your specific business workflows.',
          link:   'Get a quote',
          href:   '#development',
          accent: '#00ff9d',
        },
      ],
    },

    // ── HOSTING ──────────────────────────────────────────────
    hosting: {
      label:     'Flexible Plans',
      title:     'Hosting Packages',
      monthly:   'Monthly',
      yearly:    'Yearly',
      saveBadge: 'Save 20%',
      perMonth:  '/mo',
      choosePlan:'Choose Plan',
      popular:   'Most Popular',
      plans: [
        {
          id:           'mini',
          name:         'Mini',
          monthlyPrice: 2.95,
          yearlyPrice:  2.36,
          fullPrice:    11.95,
          desc:         'Perfect for personal sites & portfolios',
          orderUrl:     'https://billing.sityx.com/order/2',
          features: [
            '10 GB NVMe Storage',
            'Single Website Hosting',
            'Free SSL Certificate',
            'Basic Smart Protection',
            'cPanel Control Panel',
          ],
        },
        {
          id:           'start',
          name:         'Start',
          monthlyPrice: 4.90,
          yearlyPrice:  3.92,
          fullPrice:    14.95,
          desc:         'Ideal for growing businesses & blogs',
          popular:      true,
          orderUrl:     'https://billing.sityx.com/order/checkout?id=2',
          features: [
            '50 GB NVMe Gen4 Storage',
            'Unlimited Websites',
            'Free SSL Certificate',
            'Predictive AI Security',
            'Automated Daily Backups',
            'cPanel Control Panel',
          ],
        },
        {
          id:           'advanced',
          name:         'Advanced',
          monthlyPrice: 9.95,
          yearlyPrice:  7.96,
          fullPrice:    19.95,
          desc:         'For high-traffic sites & e-commerce',
          orderUrl:     'https://billing.sityx.com/order/checkout?id=3',
          features: [
            '100 GB NVMe Storage',
            'Unlimited Websites',
            'AI Resource Optimization',
            'Priority Human Support',
            'Advanced Caching System',
            'Automated Daily Backups',
            'Free SSL + Wildcard',
          ],
        },
      ],
    },

    // ── DEVELOPMENT (Infrastructure) ─────────────────────────
    development: {
      label:      'Under the Hood',
      title:      'Enterprise-Grade Infrastructure',
      subtitle:   'Every component is chosen to maximise speed, resilience, and security for your digital business.',
      stackTitle: 'Core Technologies',
      stagesTitle:'How Our AI Works',
      quoteTitle: 'Request a Migration or Quote',
      stack: [
        { icon: '⚡', name: 'NVMe Gen4' },
        { icon: '🤖', name: 'AI Engine' },
        { icon: '🌍', name: 'Global CDN' },
        { icon: '🔒', name: 'SSL / TLS' },
        { icon: '🛡️', name: 'DDoS Guard' },
        { icon: '💾', name: 'Daily Backup' },
        { icon: '📊', name: 'cPanel' },
        { icon: '🚀', name: 'LiteSpeed' },
      ],
      stages: [
        {
          n:    '01',
          title:'Continuous Monitoring',
          desc: 'Our ML algorithms analyse server hardware health and global traffic patterns 24 / 7, flagging anomalies in real time.',
        },
        {
          n:    '02',
          title:'Predictive Threat Detection',
          desc: 'By learning from global threat-intelligence networks the AI anticipates DDoS attacks, malware injections, and brute-force attempts before they occur.',
        },
        {
          n:    '03',
          title:'Auto Resource Scaling',
          desc: 'During sudden traffic spikes, the platform automatically allocates additional RAM and CPU to prevent downtime — no manual intervention needed.',
        },
        {
          n:    '04',
          title:'Instant Incident Response',
          desc: 'Threats are neutralised at the network edge. You receive real-time alerts while our system isolates and eliminates the risk.',
        },
        {
          n:    '05',
          title:'Human-Led Support',
          desc: 'Every automated action is backed by our expert team available via WhatsApp and our ticketing system for professional guidance.',
        },
      ],
      form: {
        name:           'Full Name',
        namePh:         'John Smith',
        email:          'Email Address',
        emailPh:        'hello@example.com',
        service:        'Service Type',
        serviceOptions: [
          'Web Development',
          'Mobile App',
          'Software Services',
          'Hosting',
        ],
        message:  'Project Details',
        messagePh:'Tell us about your project or requirements...',
        submit:   'Send Request',
      },
    },

    // ── DOMAIN ───────────────────────────────────────────────
    domain: {
      label:      'Find Your Name',
      title:      'Domain Registration',
      subtitle:   'Find and register your perfect domain from hundreds of extensions — managed alongside your hosting from one dashboard.',
      placeholder:'Enter your domain name...',
      searchBtn:  'Search',
      available:  'Available',
      taken:      'Taken',
      registerBtn:'Register →',
      contactBtn: 'Contact Us',
      tlds: [
        { ext: '.com',   price: '$10/yr' },
        { ext: '.net',   price: '$12/yr' },
        { ext: '.org',   price: '$11/yr' },
        { ext: '.io',    price: '$35/yr' },
        { ext: '.co',    price: '$22/yr' },
        { ext: '.sy',    price: '$40/yr' },
        { ext: '.store', price: '$8/yr'  },
        { ext: '.tech',  price: '$18/yr' },
        { ext: '.app',   price: '$16/yr' },
        { ext: '.dev',   price: '$14/yr' },
        { ext: '.me',    price: '$15/yr' },
        { ext: '.online',price: '$6/yr'  },
      ],
    },

    // ── FAQ ──────────────────────────────────────────────────
    faq: {
      label: 'Got Questions?',
      title: 'Frequently Asked Questions',
      items: [
        {
          q: 'Why choose AI-powered web hosting?',
          a: 'Traditional hosting relies on static, rigid rules. At Sityx, our machine learning algorithms continuously analyze global traffic patterns. During sudden traffic spikes, your professional cloud hosting environment automatically allocates additional RAM and CPU resources to prevent any potential downtime.',
        },
        {
          q: 'How does predictive security work?',
          a: 'Our security protocols evolve daily. By learning from global threat data networks, our AI effectively anticipates DDoS attacks, malware injections, and brute-force attempts, protecting your digital assets much more effectively than standard outdated firewalls.',
        },
        {
          q: 'Is it easy to migrate my current website to Sityx?',
          a: 'Absolutely. We provide seamless, zero-downtime website migration services completely free of charge. Our team handles the technical heavy lifting so you can focus on growing your business immediately.',
        },
        {
          q: 'What is NVMe and why is it better?',
          a: 'NVMe (Non-Volatile Memory Express) is a superior storage protocol designed specifically for modern SSDs. It allows for much faster read and write speeds compared to traditional SATA drives, significantly improving your website\'s database queries and overall load times.',
        },
        {
          q: 'How can I reach customer support?',
          a: 'We pride ourselves on offering responsive, human-led support. You can reach out to our dedicated experts via our official WhatsApp Business line or message us directly through our Facebook page for quick resolutions.',
        },
        {
          q: 'Do you offer software development services?',
          a: 'Yes — in addition to hosting, Sityx is a full-service software development agency specialising in bespoke web development, mobile applications, and custom software solutions tailored to scale your business.',
        },
      ],
    },

    // ── ABOUT ────────────────────────────────────────────────
    about: {
      label:  'Who We Are',
      title:  'The Future of Professional Cloud Hosting',
      badge:  'Software Agency + Hosting Provider',
      body: [
        'Welcome to Sityx, the modern standard for professional cloud hosting. We deeply understand that server speed, high availability, and strict security are the foundational pillars of any successful online business. That is exactly why our entire architecture is built exclusively on state-of-the-art NVMe storage drives, ensuring your websites load at lightning speed without any compromise on stability or performance.',
        'What makes our web hosting services genuinely unique is our deep integration of predictive artificial intelligence. Our smart server systems actively monitor hardware health, detect traffic spikes instantly, and block malicious cyber threats long before they can impact your valuable data. Whether you are launching a personal design portfolio, a rapidly growing technical blog, or a high-traffic e-commerce platform, our scalable cloud infrastructure seamlessly adapts to your specific requirements.',
        'When you choose our platform, you are not just buying server space; you are investing in a complete digital ecosystem. We provide a robust global Content Delivery Network (CDN) to ensure your web pages load rapidly for visitors anywhere in the world. Furthermore, our dedicated technical support team is always on standby, ready to assist you via WhatsApp or our ticketing system whenever you need professional guidance.',
        'In addition to hosting, Sityx is a full-service software development agency, specializing in bespoke web development, mobile applications, and custom software solutions tailored to scale your business.',
      ],
      features: [
        { icon: '⚡', title: 'NVMe Architecture',     desc: 'Entire infrastructure built on Gen4 NVMe drives for maximum I/O throughput.' },
        { icon: '🤖', title: 'Predictive AI',          desc: 'Smart systems monitor, adapt, and protect your assets around the clock.'       },
        { icon: '🌍', title: 'Global CDN',              desc: 'Content delivered from edge nodes worldwide for ultra-low latency.'            },
        { icon: '💻', title: 'Software Development',   desc: 'Bespoke web, mobile, and custom software solutions for every scale.'           },
      ],
    },

    // ── FOOTER ───────────────────────────────────────────────
    footer: {
      desc: 'Ultra-fast professional cloud hosting powered by AI. Secure. Fast. Reliable.',
      tagline: 'Sityx AI Cloud Architecture | Secure. Fast. Reliable.',
      services: 'Services',
      company:  'Company',
      resources:'Resources',
      legal:    'Legal',
      contact:  'Get In Touch',
      serviceLinks: [
        { label: 'Cloud Hosting',    href: 'https://test.sityx.com/shared-hosting' },
        { label: 'AI VPS Hosting',   href: 'https://test.sityx.com/vps-hosting'    },
        { label: 'Domain Names',     href: 'https://test.sityx.com/domain-names'   },
        { label: 'SSL Certificates', href: 'https://test.sityx.com/ssl-certificates'},
      ],
      companyLinks: [
        { label: 'Home',         href: 'https://test.sityx.com/'         },
        { label: 'About Sityx', href: 'https://test.sityx.com/about'    },
        { label: 'Careers',      href: 'https://test.sityx.com/careers'  },
        { label: 'Contact Us',   href: 'https://test.sityx.com/contact'  },
      ],
      resourceLinks: [
        { label: 'Our Blog',         href: 'https://test.sityx.com/blog'      },
        { label: 'Tutorials & Guides', href: 'https://test.sityx.com/tutorials'},
        { label: 'FAQ',              href: '#faq'                              },
        { label: 'Client Portal',    href: 'https://billing.sityx.com/order'  },
      ],
      legalLinks: [
        { label: 'Privacy Policy',       href: 'https://test.sityx.com/privacy-policy'   },
        { label: 'Terms of Service',     href: 'https://test.sityx.com/terms-of-service' },
        { label: 'Acceptable Use Policy',href: 'https://test.sityx.com/acceptable-use'   },
      ],
      location:  'Syria & Worldwide',
      rights:    'All rights reserved.',
      madeWith:  '© 2026 Sityx AI Cloud Architecture | Secure. Fast. Reliable.',
    },

    // ── WHATSAPP ─────────────────────────────────────────────
    wa: { tooltip: 'Chat on WhatsApp' },
  },


  // ══════════════════════════════════════════════════════════
  //  ARABIC
  // ══════════════════════════════════════════════════════════
  ar: {
    nav: {
      about:       'من نحن',
      services:    'الخدمات',
      hosting:     'باقات الاستضافة',
      development: 'البنية التحتية',
      domain:      'الدومين',
      faq:         'الأسئلة',
      contact:     'الدعم',
    },

    hero: {
      badge:    'استضافة سحابية · تطوير ويب · برمجيات',
      title1:   'استضافة سحابية فائقة السرعة',
      title2:   'وتطوير البرمجيات',
      subtitle:
        'بنية تحتية NVMe متطورة مع حماية ذكية استباقية، وخدمات تطوير ويب وبرمجيات متكاملة — مصممة للأعمال التي لا تتحمل أي توقف.',
      cta1: 'عرض باقات الاستضافة',
      cta2: 'تواصل مع الدعم',
    },

    stats: [
      { value: '99.9%', label: 'ضمان الاستمرارية' },
      { value: 'NVMe',  label: 'تخزين الجيل الرابع' },
      { value: 'AI',    label: 'حماية ذكية' },
      { value: '24/7',  label: 'دعم بشري' },
    ],

    services: {
      label:    'ما نقدمه',
      title:    'منظومة رقمية متكاملة',
      subtitle: 'أكثر من مجرد مساحة خادم — منصة ذكية تحمي أعمالك وتسرّع نموها وتبنيها.',
      items: [
        {
          icon:   '☁️',
          title:  'استضافة سحابية بالذكاء الاصطناعي',
          desc:   'تراقب خوارزمياتنا التعلمية صحة الخادم باستمرار، وتخصص الذاكرة والمعالج تلقائياً خلال ذروات الزيارات، وتحجب التهديدات قبل أن تصل لبياناتك.',
          link:   'عرض الباقات',
          href:   '#hosting',
          accent: '#00e5ff',
        },
        {
          icon:   '🛡️',
          title:  'حماية ذكية استباقية',
          desc:   'بروتوكولات أمان تتطور يومياً. يتعلم الذكاء الاصطناعي من شبكات التهديدات العالمية لاستباق هجمات DDoS وحقن البرمجيات الخبيثة وهجمات القوة الغاشمة.',
          link:   'اكتشف المزيد',
          href:   '#security',
          accent: '#7c4dff',
        },
        {
          icon:   '🌐',
          title:  'الدومينات وشهادات SSL',
          desc:   'سجّل دومينك من مئات الامتدادات وأمّن كل اتصال بشهادة SSL مجانية — كل ذلك من لوحة تحكم واحدة.',
          link:   'ابحث عن دومين',
          href:   '#domain',
          accent: '#00ff9d',
        },
        {
          icon:   '💻',
          title:  'تطوير الويب',
          desc:   'مواقع مخصصة وصفحات هبوط وتطبيقات ويب متكاملة مبنية بأحدث الأطر — React وLaravel وWordPress والمزيد.',
          link:   'احصل على عرض سعر',
          href:   '#development',
          accent: '#00e5ff',
        },
        {
          icon:   '📱',
          title:  'تطوير تطبيقات الموبايل',
          desc:   'تطبيقات موبايل متعددة المنصات وأصلية لنظامي iOS وAndroid، مصممة للأداء وسهولة الاستخدام والقابلية للتوسع.',
          link:   'احصل على عرض سعر',
          href:   '#development',
          accent: '#7c4dff',
        },
        {
          icon:   '🤖',
          title:  'تطوير أنظمة الذكاء الاصطناعي',
          desc:   'تكاملات ذكاء اصطناعي مخصصة، وخطوط أتمتة، وأدوات مدعومة بالتعلم الآلي مصممة لسير عمل أعمالك.',
          link:   'احصل على عرض سعر',
          href:   '#development',
          accent: '#00ff9d',
        },
      ],
    },

    hosting: {
      label:     'باقات مرنة',
      title:     'باقات الاستضافة',
      monthly:   'شهري',
      yearly:    'سنوي',
      saveBadge: 'وفّر 20%',
      perMonth:  '/شهر',
      choosePlan:'اختر الباقة',
      popular:   'الأكثر طلباً',
      plans: [
        {
          id:           'mini',
          name:         'Mini',
          monthlyPrice: 2.95,
          yearlyPrice:  2.36,
          fullPrice:    11.95,
          desc:         'مثالية للمواقع الشخصية والمحافظ الإبداعية',
          orderUrl:     'https://billing.sityx.com/order/2',
          features: [
            '10 جيجابايت NVMe',
            'موقع واحد',
            'شهادة SSL مجانية',
            'حماية ذكية أساسية',
            'لوحة تحكم cPanel',
          ],
        },
        {
          id:           'start',
          name:         'Start',
          monthlyPrice: 4.90,
          yearlyPrice:  3.92,
          fullPrice:    14.95,
          desc:         'مثالية للأعمال النامية والمدونات',
          popular:      true,
          orderUrl:     'https://billing.sityx.com/order/checkout?id=2',
          features: [
            '50 جيجابايت NVMe Gen4',
            'مواقع غير محدودة',
            'شهادة SSL مجانية',
            'حماية AI استباقية',
            'نسخ احتياطي يومي',
            'لوحة تحكم cPanel',
          ],
        },
        {
          id:           'advanced',
          name:         'Advanced',
          monthlyPrice: 9.95,
          yearlyPrice:  7.96,
          fullPrice:    19.95,
          desc:         'للمواقع عالية الزيارات والتجارة الإلكترونية',
          orderUrl:     'https://billing.sityx.com/order/checkout?id=3',
          features: [
            '100 جيجابايت NVMe',
            'مواقع غير محدودة',
            'تحسين موارد AI',
            'دعم بشري أولوية',
            'نظام تخزين مؤقت متقدم',
            'نسخ احتياطي يومي',
            'SSL + Wildcard مجاني',
          ],
        },
      ],
    },

    development: {
      label:      'تحت الغطاء',
      title:      'بنية تحتية بمستوى المؤسسات',
      subtitle:   'كل مكوّن مختار بعناية لتعظيم السرعة والمرونة والأمان لأعمالك الرقمية.',
      stackTitle: 'التقنيات الأساسية',
      stagesTitle:'كيف يعمل الذكاء الاصطناعي',
      quoteTitle: 'طلب نقل أو عرض سعر',
      stack: [
        { icon: '⚡', name: 'NVMe Gen4' },
        { icon: '🤖', name: 'محرك AI' },
        { icon: '🌍', name: 'CDN عالمي' },
        { icon: '🔒', name: 'SSL / TLS' },
        { icon: '🛡️', name: 'حماية DDoS' },
        { icon: '💾', name: 'نسخ يومي' },
        { icon: '📊', name: 'cPanel' },
        { icon: '🚀', name: 'LiteSpeed' },
      ],
      stages: [
        {
          n:    '01',
          title:'مراقبة مستمرة',
          desc: 'تحلل خوارزمياتنا التعلمية صحة الخادم وأنماط حركة المرور العالمية على مدار الساعة، وتكشف الشذوذات في الوقت الفعلي.',
        },
        {
          n:    '02',
          title:'كشف التهديدات الاستباقي',
          desc: 'بالتعلم من شبكات الاستخبارات التهديدية العالمية، يستبق الذكاء الاصطناعي هجمات DDoS وحقن البرمجيات الخبيثة وهجمات القوة الغاشمة قبل وقوعها.',
        },
        {
          n:    '03',
          title:'توسيع الموارد التلقائي',
          desc: 'خلال ذروات الزيارات المفاجئة، تخصص المنصة تلقائياً ذاكرة ومعالجاً إضافيين لمنع أي توقف — دون أي تدخل يدوي.',
        },
        {
          n:    '04',
          title:'الاستجابة الفورية للحوادث',
          desc: 'تُحيَّد التهديدات عند حافة الشبكة. تتلقى تنبيهات فورية بينما يعزل النظام الخطر ويقضي عليه.',
        },
        {
          n:    '05',
          title:'دعم بشري متخصص',
          desc: 'كل إجراء آلي مدعوم بفريق خبرائنا المتاح عبر واتساب ونظام التذاكر للإرشاد المهني.',
        },
      ],
      form: {
        name:           'الاسم الكامل',
        namePh:         'محمد أحمد',
        email:          'البريد الإلكتروني',
        emailPh:        'hello@example.com',
        service:        'نوع الخدمة',
        serviceOptions: [
          'تطوير ويب',
          'تطبيق موبايل',
          'خدمات برمجية',
          'استضافة',
        ],
        message:  'تفاصيل المشروع',
        messagePh:'أخبرنا عن مشروعك أو متطلباتك...',
        submit:   'إرسال الطلب',
      },
    },

    domain: {
      label:      'اعثر على اسمك',
      title:      'تسجيل الدومين',
      subtitle:   'ابحث وسجّل دومينك المثالي من مئات الامتدادات — تُدار جنباً إلى جنب مع استضافتك من لوحة واحدة.',
      placeholder:'أدخل اسم الدومين...',
      searchBtn:  'بحث',
      available:  'متاح',
      taken:      'محجوز',
      registerBtn:'← سجّل',
      contactBtn: 'تواصل معنا',
      tlds: [
        { ext: '.com',   price: '$10/yr' },
        { ext: '.net',   price: '$12/yr' },
        { ext: '.org',   price: '$11/yr' },
        { ext: '.io',    price: '$35/yr' },
        { ext: '.co',    price: '$22/yr' },
        { ext: '.sy',    price: '$40/yr' },
        { ext: '.store', price: '$8/yr'  },
        { ext: '.tech',  price: '$18/yr' },
        { ext: '.app',   price: '$16/yr' },
        { ext: '.dev',   price: '$14/yr' },
        { ext: '.me',    price: '$15/yr' },
        { ext: '.online',price: '$6/yr'  },
      ],
    },

    faq: {
      label: 'لديك أسئلة؟',
      title: 'الأسئلة الشائعة',
      items: [
        {
          q: 'لماذا تختار الاستضافة المدعومة بالذكاء الاصطناعي؟',
          a: 'الاستضافة التقليدية تعتمد على قواعد ثابتة وصارمة. في Sityx، تحلل خوارزمياتنا التعلمية أنماط حركة المرور العالمية باستمرار. وعند حدوث ذروات مفاجئة، تخصص بيئة الاستضافة تلقائياً ذاكرة وموارد معالج إضافية لمنع أي توقف محتمل.',
        },
        {
          q: 'كيف تعمل الحماية الاستباقية؟',
          a: 'تتطور بروتوكولات الأمان لدينا يومياً. بالتعلم من شبكات بيانات التهديدات العالمية، يستبق ذكاؤنا الاصطناعي هجمات DDoS وحقن البرمجيات الخبيثة وهجمات القوة الغاشمة، مما يحمي أصولك الرقمية بفاعلية أكبر بكثير من جدران الحماية التقليدية القديمة.',
        },
        {
          q: 'هل يسهل نقل موقعي الحالي إلى Sityx؟',
          a: 'بالتأكيد. نقدم خدمات نقل مواقع سلسة وبدون توقف بالكامل مجاناً. يتولى فريقنا العمل التقني الشاق حتى تتمكن من التركيز على تنمية أعمالك فوراً.',
        },
        {
          q: 'ما هو NVMe ولماذا هو أفضل؟',
          a: 'NVMe (Non-Volatile Memory Express) هو بروتوكول تخزين متطور مصمم خصيصاً لأقراص SSD الحديثة. يتيح سرعات قراءة وكتابة أسرع بكثير مقارنة بمحركات SATA التقليدية، مما يحسن بشكل ملحوظ استعلامات قواعد البيانات وأوقات تحميل موقعك.',
        },
        {
          q: 'كيف أتواصل مع الدعم الفني؟',
          a: 'نفخر بتقديم دعم بشري متجاوب. يمكنك التواصل مع خبرائنا المتخصصين عبر خط واتساب الرسمي أو مراسلتنا مباشرة عبر صفحتنا على فيسبوك للحصول على حلول سريعة.',
        },
        {
          q: 'هل تقدمون خدمات تطوير البرمجيات؟',
          a: 'نعم — إضافة إلى الاستضافة، تُعدّ Sityx وكالة تطوير برمجيات متكاملة متخصصة في تطوير الويب المخصص، وتطبيقات الموبايل، وحلول البرمجيات المخصصة المصممة لتوسيع نطاق أعمالك.',
        },
      ],
    },

    // ── ABOUT ────────────────────────────────────────────────
    about: {
      label:  'من نحن',
      title:  'مستقبل الاستضافة السحابية الاحترافية',
      badge:  'وكالة برمجيات + مزود استضافة',
      body: [
        'مرحباً بك في Sityx، المعيار الحديث للاستضافة السحابية الاحترافية. ندرك تماماً أن سرعة الخادم وتوافره العالي وأمانه المحكم هي الركائز الأساسية لأي أعمال ناجحة على الإنترنت. ولهذا بنينا بنيتنا التحتية بالكامل على أحدث محركات تخزين NVMe، لضمان تحميل مواقعك بسرعة البرق دون أي تنازل عن الاستقرار أو الأداء.',
        'ما يجعل خدمات استضافة الويب لدينا فريدة هو تكاملنا العميق مع الذكاء الاصطناعي الاستباقي. تراقب أنظمتنا الذكية صحة الخادم باستمرار، وترصد ذروات الزيارات فوراً، وتحجب التهديدات الإلكترونية الخبيثة قبل أن تؤثر على بياناتك. سواء كنت تطلق محفظة تصميمية شخصية أو مدونة تقنية متنامية أو منصة تجارة إلكترونية عالية الزيارات، فإن بنيتنا السحابية القابلة للتوسع تتكيف بسلاسة مع متطلباتك.',
        'حين تختار منصتنا، فأنت لا تشتري مساحة خادم فحسب؛ بل تستثمر في منظومة رقمية متكاملة. نوفر شبكة توصيل محتوى (CDN) عالمية قوية لضمان تحميل صفحاتك بسرعة لزوار من كل مكان. علاوة على ذلك، فريق الدعم التقني المتخصص لدينا دائماً في الخدمة، جاهز لمساعدتك عبر واتساب أو نظام التذاكر.',
        'إضافة إلى الاستضافة، تُعدّ Sityx وكالة تطوير برمجيات متكاملة متخصصة في تطوير الويب المخصص، وتطبيقات الموبايل، وحلول البرمجيات المصممة لتوسيع نطاق أعمالك.',
      ],
      features: [
        { icon: '⚡', title: 'بنية NVMe',              desc: 'بنية تحتية كاملة على محركات NVMe Gen4 لأقصى إنتاجية ممكنة.'   },
        { icon: '🤖', title: 'ذكاء اصطناعي استباقي', desc: 'أنظمة ذكية تراقب وتتكيف وتحمي أصولك على مدار الساعة.'          },
        { icon: '🌍', title: 'CDN عالمي',              desc: 'توصيل المحتوى من عقد حافة حول العالم لزمن استجابة منخفض جداً.' },
        { icon: '💻', title: 'تطوير البرمجيات',       desc: 'حلول ويب وموبايل وبرمجيات مخصصة لكل حجم من الأعمال.'          },
      ],
    },

    // ── FOOTER ───────────────────────────────────────────────
    footer: {
      desc:     'استضافة سحابية احترافية فائقة السرعة مدعومة بالذكاء الاصطناعي. آمنة. سريعة. موثوقة.',
      tagline:  'Sityx — بنية سحابية ذكية | آمنة. سريعة. موثوقة.',
      services: 'الخدمات',
      company:  'الشركة',
      resources:'الموارد',
      legal:    'القانونية',
      contact:  'تواصل معنا',
      serviceLinks: [
        { label: 'الاستضافة السحابية', href: 'https://test.sityx.com/shared-hosting'  },
        { label: 'VPS بالذكاء الاصطناعي', href: 'https://test.sityx.com/vps-hosting' },
        { label: 'أسماء الدومينات',    href: 'https://test.sityx.com/domain-names'    },
        { label: 'شهادات SSL',          href: 'https://test.sityx.com/ssl-certificates'},
      ],
      companyLinks: [
        { label: 'الرئيسية',    href: 'https://test.sityx.com/'       },
        { label: 'عن Sityx',   href: 'https://test.sityx.com/about'  },
        { label: 'الوظائف',    href: 'https://test.sityx.com/careers'},
        { label: 'تواصل معنا', href: 'https://test.sityx.com/contact'},
      ],
      resourceLinks: [
        { label: 'المدونة',           href: 'https://test.sityx.com/blog'      },
        { label: 'الدروس والأدلة',    href: 'https://test.sityx.com/tutorials' },
        { label: 'الأسئلة الشائعة',  href: '#faq'                              },
        { label: 'بوابة العملاء',    href: 'https://billing.sityx.com/order'  },
      ],
      legalLinks: [
        { label: 'سياسة الخصوصية',     href: 'https://test.sityx.com/privacy-policy'   },
        { label: 'شروط الخدمة',        href: 'https://test.sityx.com/terms-of-service' },
        { label: 'سياسة الاستخدام المقبول', href: 'https://test.sityx.com/acceptable-use'},
      ],
      location: 'سوريا وحول العالم',
      rights:   'جميع الحقوق محفوظة.',
      madeWith: '© 2026 Sityx — بنية سحابية ذكية | آمنة. سريعة. موثوقة.',
    },

    wa: { tooltip: 'تحدث عبر واتساب' },
  },
};
